import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Immutable from 'immutable';

import {
  commerceCartApplyCoupon,
  commerceCartRemoveCoupon,
} from '../../../store/actions/Commerce';
import useTypedDispatch from '../../../hooks/useTypedDispatch';
import {
  getCouponEligibility,
  getCouponDescription,
  getAppliedCouponLabel,
} from './helpers';

import './style.scss';

interface DiscountCodeSectionProps {
  /**
   * Whether the order is being placed for someone else. A coupon is personal
   * and cannot be combined with the "voor iemand anders" flow.
   */
  forSomeoneElse?: boolean;
  /**
   * Which checkout hosts the section. On checkout v1 the quantity is changed on
   * a separate cart page, so the multi-product block adds a call to action to
   * adjust the order there; v2 is single-page and omits it.
   */
  variant?: 'v1' | 'v2';
}

/**
 * Coupon-code entry and applied state for the checkout order summary.
 *
 * A coupon is only valid for a single product line at quantity 1, placed for
 * the customer themselves and not free. When those conditions are not met the
 * field is blocked (collapsed with an explanatory helper, or hidden for free
 * products). On checkout V2 the cart can change while a coupon is applied; the
 * coupon is then shown as inactive with a call to action and re-applied
 * automatically once the cart becomes eligible again.
 */
const DiscountCodeSection: React.FC<DiscountCodeSectionProps> = ({
  forSomeoneElse = false,
  variant = 'v2',
}) => {
  const { t } = useTranslation();
  const dispatch = useTypedDispatch();

  const products = useSelector((state: any) => state.getIn(['commerce', 'cart', 'products'], Immutable.List()));

  const [isOpen, setIsOpen] = useState(false);
  const [code, setCode] = useState('');
  const [appliedCode, setAppliedCode] = useState('');
  const [inactiveCode, setInactiveCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isApplied, setIsApplied] = useState(false);

  const { isFree, isEligible, blockReason } = useMemo(
    () => getCouponEligibility(products, forSomeoneElse),
    [products, forSomeoneElse],
  );

  // The per-coupon thank-you message, exposed by the backend on the promotion
  // adjustment. Falls back to the generic message when none is configured.
  const couponDescription = useMemo<string | null>(
    () => getCouponDescription(products),
    [products],
  );

  // Label of a coupon already applied to the cart (e.g. on page reload), read
  // from the cart's promotion adjustments.
  const appliedCouponLabel = useMemo<string | null>(
    () => getAppliedCouponLabel(products),
    [products],
  );

  // Guards against re-entrancy while an apply/remove round-trips and reloads
  // the cart (which produces a new products reference and re-runs effects).
  const conflictHandled = useRef(false);

  // Reflect a coupon already on the cart exactly once, after the cart first
  // loads — restoring the applied state on a page refresh (the discount shows in
  // the summary, so the section must show it as active too). The entered code is
  // not in the cart payload, so the promotion label stands in for it. Gated by a
  // ref so it never re-fires after a remove, whose cart reload briefly still
  // carries the old adjustment.
  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current || products.size === 0) return;
    restoredRef.current = true;
    if (appliedCouponLabel && !isApplied) {
      setAppliedCode(appliedCouponLabel);
      setIsApplied(true);
    }
  }, [products, appliedCouponLabel, isApplied]);

  // Live conflict: a coupon was applied and the cart became ineligible.
  // Drop the server-side coupon so totals are correct and show it as inactive.
  useEffect(() => {
    if (isApplied && !isEligible && !conflictHandled.current) {
      conflictHandled.current = true;
      dispatch(commerceCartRemoveCoupon()).catch(() => {});
      setInactiveCode(appliedCode);
      setIsApplied(false);
      setIsOpen(false);
      setError(null);
    }
  }, [isApplied, isEligible, appliedCode, dispatch]);

  // Conflict resolved: the cart is eligible again, so re-apply the code.
  useEffect(() => {
    if (isEligible && inactiveCode && !isApplied && !isLoading) {
      setIsLoading(true);
      dispatch(commerceCartApplyCoupon(inactiveCode))
        .then(() => {
          setAppliedCode(inactiveCode);
          setIsApplied(true);
          setInactiveCode('');
          conflictHandled.current = false;
        })
        .catch(() => {
          setInactiveCode('');
          conflictHandled.current = false;
          setError(t('cart.discount_code_invalid'));
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [isEligible, inactiveCode, isApplied, isLoading, dispatch, t]);

  const handleApply = useCallback(() => {
    if (!code.trim()) return;

    setIsLoading(true);
    setError(null);

    dispatch(commerceCartApplyCoupon(code.trim()))
      .then(() => {
        setAppliedCode(code.trim());
        setIsApplied(true);
        setIsOpen(false);
        setCode('');
      })
      .catch(() => {
        setError(t('cart.discount_code_invalid'));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [code, dispatch, t]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') handleApply();
    },
    [handleApply],
  );

  const handleRemove = useCallback(() => {
    setIsLoading(true);
    dispatch(commerceCartRemoveCoupon())
      .then(() => {
        setIsApplied(false);
        setAppliedCode('');
      })
      .catch(() => {
        setIsApplied(false);
        setAppliedCode('');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [dispatch]);

  const handleClearInactive = useCallback(() => {
    setInactiveCode('');
    conflictHandled.current = false;
  }, []);

  // Free product: a coupon is never offered.
  if (isFree) {
    return null;
  }

  // Live conflict: code applied, then the cart became ineligible.
  if (inactiveCode) {
    return (
      <div className="DiscountCodeSection DiscountCodeSection--inactive">
        <div className="DiscountCodeSection__active-header">
          <span className="DiscountCodeSection__active-label">
            {t('cart.discount_code_label')}
          </span>
        </div>
        <div className="DiscountCodeSection__active-row DiscountCodeSection__active-row--inactive">
          <span className="DiscountCodeSection__active-code">
            {inactiveCode}
          </span>
          <button
            type="button"
            className="DiscountCodeSection__active-remove"
            onClick={handleClearInactive}
            aria-label={t('cart.discount_code_remove')}
          >
            ×
          </button>
        </div>
        <p className="DiscountCodeSection__helper">
          {`${t('cart.discount_code_blocked_multi')} ${t(
            'cart.discount_code_conflict_cta',
          )}`}
        </p>
      </div>
    );
  }

  // Applied: show the active code and its thank-you message.
  if (isApplied) {
    return (
      <div className="DiscountCodeSection DiscountCodeSection--applied">
        <div className="DiscountCodeSection__active-header">
          <span className="DiscountCodeSection__active-label">
            {t('cart.discount_code_label')}
          </span>
          <span className="DiscountCodeSection__active-badge">
            {t('cart.discount_code_active')}
          </span>
        </div>
        <div className="DiscountCodeSection__active-row">
          <span className="DiscountCodeSection__active-code">
            {appliedCode}
          </span>
          <button
            type="button"
            className="DiscountCodeSection__active-remove"
            onClick={handleRemove}
            disabled={isLoading}
            aria-label={t('cart.discount_code_remove')}
          >
            ×
          </button>
        </div>
        <p className="DiscountCodeSection__thank-you">
          {couponDescription || t('cart.discount_code_thank_you')}
        </p>
      </div>
    );
  }

  // Blocked entry: collapsed header with an explanatory helper. On checkout v1
  // the quantity is changed on the separate cart page, so the multi-product
  // block points the customer there; v2 is single-page and omits that CTA.
  if (blockReason) {
    let helper = t('cart.discount_code_blocked_multi');
    if (blockReason === 'someone_else') {
      helper = t('cart.discount_code_blocked_someone_else');
    } else if (variant === 'v1') {
      helper = `${t('cart.discount_code_blocked_multi')} ${t(
        'cart.discount_code_conflict_cta',
      )}`;
    }
    return (
      <div className="DiscountCodeSection DiscountCodeSection--blocked">
        <span className="DiscountCodeSection__toggle DiscountCodeSection__toggle--blocked">
          {t('cart.add_discount_code')}
          <span className="DiscountCodeSection__toggle__icon">+</span>
        </span>
        <p className="DiscountCodeSection__helper">{helper}</p>
      </div>
    );
  }

  // Eligible: the collapsed toggle and the entry field.
  return (
    <div className="DiscountCodeSection">
      {!isOpen && (
        <button
          type="button"
          className="DiscountCodeSection__toggle"
          onClick={() => {
            setIsOpen(true);
            setError(null);
          }}
        >
          {t('cart.add_discount_code')}
          <span className="DiscountCodeSection__toggle__icon">+</span>
        </button>
      )}

      {isOpen && (
        <div className="DiscountCodeSection__form">
          <span className="DiscountCodeSection__field-label">
            {t('cart.discount_code_label')}
          </span>
          <div className="DiscountCodeSection__input-row">
            <div
              className={`DiscountCodeSection__input-wrapper${
                error ? ' DiscountCodeSection__input-wrapper--error' : ''
              }`}
            >
              <input
                type="text"
                className="DiscountCodeSection__input"
                placeholder={t('cart.discount_code_input_placeholder')}
                value={code}
                onChange={(e) => {
                  setCode(e.target.value);
                  setError(null);
                }}
                onKeyDown={handleKeyDown}
                disabled={isLoading}
                autoComplete="off"
              />
              {code && (
                <button
                  type="button"
                  className="DiscountCodeSection__clear"
                  onClick={() => {
                    setCode('');
                    setError(null);
                  }}
                  tabIndex={-1}
                  aria-label={t('cart.discount_code_clear')}
                >
                  ×
                </button>
              )}
            </div>
            <button
              type="button"
              className="DiscountCodeSection__submit"
              disabled={isLoading || !code.trim()}
              onClick={handleApply}
            >
              {isLoading
                ? t('cart.discount_code_checking')
                : t('cart.discount_code_apply')}
            </button>
          </div>
          {error && (
            <div className="DiscountCodeSection__error" role="alert">
              {error}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DiscountCodeSection;
